import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./umpireSchedule.css";

function UmpireSchedule() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const game = location.state?.game;

  const [tournamentName, setTournamentName] = useState("");
  const [comments, setComments] = useState("");
  const [authToken, setAuthToken] = useState("");
  
  // Card tracking states
  const [cardActions, setCardActions] = useState([]);
  const [selectedCard, setSelectedCard] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [cardReason, setCardReason] = useState("");
  const [showCardModal, setShowCardModal] = useState(false);
  
  // Timer states
  const [seconds, setSeconds] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const timerRef = useRef(null);
  
  // Set scores for each player and set
  const [sets, setSets] = useState([
    { 
      player1Score: 0, 
      player2Score: 0, 
      player1Service: true, 
      player2Service: false,
      winner: null,
      locked: false
    },
    { 
      player1Score: 0, 
      player2Score: 0, 
      player1Service: false, 
      player2Service: false,
      winner: null,
      locked: false
    },
    { 
      player1Score: 0, 
      player2Score: 0, 
      player1Service: false, 
      player2Service: false,
      winner: null,
      locked: false
    }
  ]);

  const [matchComplete, setMatchComplete] = useState(false);
  const [sidesSwapped, setSidesSwapped] = useState(false);

  // Get match ID
  const matchId = game?.id || id || "39e1a3db-1505-43fa-9631-12ee744807b6";

  // Load token from storage
  useEffect(() => {
    const token = 
      location.state?.token || 
      localStorage.getItem('authToken') || 
      sessionStorage.getItem('authToken') ||
      localStorage.getItem('token') || 
      sessionStorage.getItem('token') ||
      "";
    
    if (token) {
      setAuthToken(token);
    } else {
      console.warn('No authentication token found');
    }
  }, [location.state]);

  // Initialize tournament name from game data
  useEffect(() => {
    if (game) {
      setTournamentName(game.tournamentName || "");
      console.log('Game Object:', game);
      console.log('Available member IDs:', {
        side1Id: game.side1Id,
        side2Id: game.side2Id,
        team1MemberId: game.team1MemberId,
        team2MemberId: game.team2MemberId,
        player1Id: game.player1Id,
        player2Id: game.player2Id,
        team1Id: game.team1Id,
        team2Id: game.team2Id,
        side1Label: game.side1Label,
        side2Label: game.side2Label
      });
    }
  }, [game]);

  // Card types - Only 3 cards
  const cardTypes = [
    { name: "Yellow Card", color: "#FFC107" },
    { name: "Red Card", color: "#dc3545" },
    { name: "Black Card", color: "#000000" }
  ];

  // Card reasons
  const cardReasons = [
    "Misconduct",
    "Unsportsmanlike behavior",
    "Delay of game",
    "Equipment violation",
    "Technical foul",
    "Other"
  ];

  // Timer effect
  useEffect(() => {
    if (isTimerRunning) {
      timerRef.current = setInterval(() => {
        setSeconds(prevSeconds => prevSeconds + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isTimerRunning]);

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateTimeString;
    }
  };

  const handleStartTimer = () => {
    setIsTimerRunning(true);
    setHasStarted(true);
  };

  const handlePauseTimer = () => {
    setIsTimerRunning(false);
  };

  // Generate GUID helper function
  const generateGuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // API call function to update match scoring
  const updateMatchScoring = async (setIndex, updatedSet) => {
    try {
      // Only send sets up to and including the current set being updated
      const setsToSend = sets.map((set, index) => {
        const currentSet = index === setIndex ? updatedSet : set;
        
        // Determine currentServerSide based on service
        let currentServerSide = 0;
        if (currentSet.player1Service) {
          currentServerSide = 1;
        } else if (currentSet.player2Service) {
          currentServerSide = 2;
        }

        // Get member IDs - prioritize the ones that are actually member IDs (GUIDs)
        const side1Id = game?.side1Id || game?.team1MemberId || game?.player1Id || game?.team1Id || "";
        const side2Id = game?.side2Id || game?.team2MemberId || game?.player2Id || game?.team2Id || "";
        
        return {
          id: generateGuid(),
          setNumber: index + 1,
          side1: side1Id,
          side2: side2Id,
          side1Score: currentSet.player1Score,
          side2Score: currentSet.player2Score,
          currentServerSide: currentServerSide,
          winnerSide: currentSet.winner || 0,
          isSetDone: currentSet.locked,
          isSetActive: index === setIndex || (currentSet.player1Score > 0 || currentSet.player2Score > 0)
        };
      });

      const apiPayload = {
        sets: setsToSend,
        status: matchComplete ? "Completed" : "In Progress",
        umpireData: JSON.stringify({
          matchNumber: game?.matchNumber,
          matchDuration: formatTime(seconds),
          totalSeconds: seconds,
          cardActions: cardActions,
          sidesSwapped: sidesSwapped
        }),
        overallData: JSON.stringify({
          sets: sets.map((set, idx) => ({
            setNumber: idx + 1,
            player1Score: set.player1Score,
            player2Score: set.player2Score,
            winner: set.winner
          }))
        }),
        remarks: comments || "",
        matchStatus: matchComplete ? "Completed" : "In Progress"
      };

      const response = await fetch(
        `https://api.asterickx.com/api/MatchScoring/${matchId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify(apiPayload)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        console.error('Payload sent:', JSON.stringify(apiPayload, null, 2));
        throw new Error(`API Error: ${response.status}`);
      }

      const result = await response.json();
      console.log('Match data updated successfully:', result);
      return result;
    } catch (error) {
      console.error('Error updating match scoring:', error);
      return null;
    }
  };

  const handleAddCard = () => {
    if (!selectedCard || !selectedPlayer || !cardReason) {
      alert("Please select player and reason!");
      return;
    }

    const cardType = cardTypes.find(c => c.name === selectedCard);
    const newCardAction = {
      id: Date.now(),
      card: cardType,
      player: selectedPlayer,
      reason: cardReason,
      timestamp: formatTime(seconds),
      time: new Date().toLocaleTimeString()
    };

    setCardActions([...cardActions, newCardAction]);
    setShowCardModal(false);
    setSelectedCard("");
    setSelectedPlayer("");
    setCardReason("");
  };

  const handleDeleteCard = (id) => {
    if (window.confirm("Are you sure you want to delete this card?")) {
      setCardActions(cardActions.filter(action => action.id !== id));
    }
  };

  useEffect(() => {
    if (!game) {
      navigate('/listofgame');
    }
  }, [game, navigate]);

  if (!game) {
    return null;
  }

  const isSet3Disabled = () => {
    const set1Winner = sets[0].winner;
    const set2Winner = sets[1].winner;
    if (set1Winner && set2Winner && set1Winner === set2Winner) {
      return true;
    }
    return false;
  };

  const handleScoreChange = async (setIndex, player, action) => {
    if (matchComplete || (setIndex === 2 && isSet3Disabled()) || sets[setIndex].locked) return;
    
    // Check if previous sets are completed before allowing updates
    if (setIndex === 1 && !sets[0].locked) {
      alert('Please complete Set 1 first by clicking the Win button!');
      return;
    }
    if (setIndex === 2 && !sets[1].locked) {
      alert('Please complete Set 2 first by clicking the Win button!');
      return;
    }
    
    const newSets = [...sets];
    const scoreKey = `player${player}Score`;
    
    if (action === 'increment') {
      newSets[setIndex][scoreKey] += 1;
    } else if (action === 'decrement' && newSets[setIndex][scoreKey] > 0) {
      newSets[setIndex][scoreKey] -= 1;
    }
    
    setSets(newSets);
    
    // Call API to update the score
    await updateMatchScoring(setIndex, newSets[setIndex]);
  };

  const handleServiceToggle = async (setIndex, player) => {
    if (matchComplete || (setIndex === 2 && isSet3Disabled()) || sets[setIndex].locked) return;
    
    // Check if previous sets are completed before allowing updates
    if (setIndex === 1 && !sets[0].locked) {
      alert('Please complete Set 1 first by clicking the Win button!');
      return;
    }
    if (setIndex === 2 && !sets[1].locked) {
      alert('Please complete Set 2 first by clicking the Win button!');
      return;
    }
    
    const newSets = [...sets];
    newSets[setIndex].player1Service = player === 1;
    newSets[setIndex].player2Service = player === 2;
    setSets(newSets);
    
    // Call API to update the service
    await updateMatchScoring(setIndex, newSets[setIndex]);
  };

  const handleSetWin = async (setIndex, player) => {
    if (matchComplete || (setIndex === 2 && isSet3Disabled()) || sets[setIndex].locked) return;
    
    // Check if previous sets are completed before allowing updates
    if (setIndex === 1 && !sets[0].locked) {
      alert('Please complete Set 1 first by clicking the Win button!');
      return;
    }
    if (setIndex === 2 && !sets[1].locked) {
      alert('Please complete Set 2 first by clicking the Win button!');
      return;
    }
    
    const newSets = [...sets];
    newSets[setIndex].winner = player;
    newSets[setIndex].locked = true;
    setSets(newSets);
    
    // Call API to update the winner
    await updateMatchScoring(setIndex, newSets[setIndex]);
  };

  const handleSwapSides = () => {
    setSidesSwapped(!sidesSwapped);
  };

  const handleMatchComplete = async () => {
    if (!hasStarted) {
      alert('Please start the timer before completing the match!');
      return;
    }

    const set1Winner = sets[0].winner;
    const set2Winner = sets[1].winner;
    const set3Winner = sets[2].winner;

    if (!set1Winner || !set2Winner) {
      alert('Please complete at least first two sets!');
      return;
    }

    if (set1Winner !== set2Winner && !set3Winner) {
      alert('Set 3 must be completed!');
      return;
    }

    setIsTimerRunning(false);
    setMatchComplete(true);
    
    // Determine overall winner
    let overallWinner = 0;
    const player1Wins = sets.filter(s => s.winner === 1).length;
    const player2Wins = sets.filter(s => s.winner === 2).length;
    if (player1Wins > player2Wins) {
      overallWinner = 1;
    } else if (player2Wins > player1Wins) {
      overallWinner = 2;
    }

    // Prepare API payload according to the schema
    const apiPayload = {
      sets: sets.map((set, index) => {
        // Determine currentServerSide based on service
        let currentServerSide = 0;
        if (set.player1Service) {
          currentServerSide = 1;
        } else if (set.player2Service) {
          currentServerSide = 2;
        }

        // Get member IDs - prioritize the ones that are actually member IDs (GUIDs)
        const side1Id = game?.side1Id || game?.team1MemberId || game?.player1Id || game?.team1Id || "";
        const side2Id = game?.side2Id || game?.team2MemberId || game?.player2Id || game?.team2Id || "";

        return {
          id: generateGuid(),
          setNumber: index + 1,
          side1: side1Id,
          side2: side2Id,
          side1Score: set.player1Score,
          side2Score: set.player2Score,
          currentServerSide: currentServerSide,
          winnerSide: set.winner || 0,
          isSetDone: set.locked,
          isSetActive: true  // Always send as active to allow updates
        };
      }),
      status: "Completed",
      umpireData: JSON.stringify({
        matchNumber: game.matchNumber,
        matchDuration: formatTime(seconds),
        totalSeconds: seconds,
        cardActions: cardActions,
        sidesSwapped: sidesSwapped
      }),
      overallData: JSON.stringify({
        sets: sets.map((set, idx) => ({
          setNumber: idx + 1,
          player1Score: set.player1Score,
          player2Score: set.player2Score,
          winner: set.winner
        })),
        player1TotalWins: player1Wins,
        player2TotalWins: player2Wins
      }),
      remarks: comments || "",
      matchStatus: "Completed",
      overallStatus: "Finished",
      winnerSide: overallWinner
    };

    try {
      // Send data to API
      const response = await fetch(
        `https://api.asterickx.com/api/MatchScoring/${matchId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify(apiPayload)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      console.log('Match Data Saved Successfully:', {
        matchId: matchId,
        game: game,
        sets: sets,
        comments: comments,
        cardActions: cardActions,
        tournamentName: tournamentName,
        matchDuration: formatTime(seconds),
        totalSeconds: seconds,
        apiPayload: apiPayload,
        apiResponse: result
      });

      alert(`Match completed and saved successfully!\nMatch Number: ${game.matchNumber || 'N/A'}\nMatch Duration: ${formatTime(seconds)}\nWinner: Player ${overallWinner || 'TBD'}`);
      navigate('/listofgame');
    } catch (error) {
      console.error('Error saving match data:', error);
      alert(`Error saving match data: ${error.message}\nPlease try again or contact support.`);
      setMatchComplete(false);
      setIsTimerRunning(true);
    }
  };

  const formatPlayerDisplay = (game) => {
    if (!game) return { player1: "N/A", player2: "N/A" };
    
    // For team/doubles matches - team names are used
    if (game.isTeamMatch) {
      return {
        player1: game.team1Name || "N/A",
        player2: game.team2Name || "N/A"
      };
    }
    
    // For singles matches
    return {
      player1: game.team1Name || "N/A",
      player2: game.team2Name || "N/A"
    };
  };

  const players = formatPlayerDisplay(game);
  const set3Disabled = isSet3Disabled();

  const getPlayerData = (side) => {
    if (side === 'left') {
      return sidesSwapped ? {
        data: players.player2,
        isPlayer1: false
      } : {
        data: players.player1,
        isPlayer1: true
      };
    } else {
      return sidesSwapped ? {
        data: players.player1,
        isPlayer1: true
      } : {
        data: players.player2,
        isPlayer1: false
      };
    }
  };

  const leftPlayer = getPlayerData('left');
  const rightPlayer = getPlayerData('right');

  return (
    <div className="umpire-schedule-page">
      {/* Timer - Top Right Corner */}
      <div className={`timer-widget ${!isTimerRunning && hasStarted ? 'paused' : ''} ${matchComplete ? 'stopped' : ''}`}>
        <div className="timer-icon">⏱️</div>
        <div className="timer-display">{formatTime(seconds)}</div>
        {!hasStarted ? (
          <button className="timer-play-btn" onClick={handleStartTimer} disabled={matchComplete}>▶</button>
        ) : (
          <>
            {isTimerRunning ? (
              <button className="timer-play-btn" onClick={handlePauseTimer} disabled={matchComplete}>⏸</button>
            ) : (
              <button className="timer-play-btn" onClick={handleStartTimer} disabled={matchComplete}>▶</button>
            )}
          </>
        )}
      </div>

      <div className="container-fluid px-3 px-lg-5 py-4">
        {/* Sticky Header Container */}
        <div className="sticky-header">
         
          {/* <div className="match-info-bar">
            <div className="info-item">
              <div className="info-label">Tournament:</div>
              <div className="info-text">{game.tournamentName || "N/A"}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Match No:</div>
              <div className="info-text">{game.matchNumber || "N/A"}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Event:</div>
              <div className="info-text">{game.eventName || "N/A"}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Round:</div>
              <div className="info-text">{game.roundName || "N/A"}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Court:</div>
              <div className="info-text">{game.courtNumber || "N/A"}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Date & Time:</div>
              <div className="info-text">{formatDateTime(game.matchDateTime)}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Status:</div>
              <div className="info-text">
                <span className={`status-badge status-${game.status?.toLowerCase()}`}>
                  {game.status || "N/A"}
                </span>
              </div>
            </div>
          </div> */}

          {/* Players Section */}
          <div className="players-container">
            <div className="player-box-left">
              <div className="player-label-top">Player {leftPlayer.isPlayer1 ? '1' : '2'}</div>
              <div className="player-name-main">{leftPlayer.data}</div>
              {game.isTeamMatch && <div className="team-badge-label">Team Match</div>}
            </div>

            <div className="vs-section">
              <div className="vs-badge">VS</div>
              <button className="swap-sides-btn" onClick={handleSwapSides} disabled={matchComplete}>
                ⇄ Swap Sides
              </button>
            </div>

            <div className="player-box-right">
              <div className="player-label-top">Player {rightPlayer.isPlayer1 ? '1' : '2'}</div>
              <div className="player-name-main">{rightPlayer.data}</div>
              {game.isTeamMatch && <div className="team-badge-label">Team Match</div>}
            </div>
          </div>
        </div>

        {/* Sets Section */}
        {sets.map((set, index) => {
          const effectivePlayer1 = sidesSwapped ? 2 : 1;
          const effectivePlayer2 = sidesSwapped ? 1 : 2;
          const player1Score = sidesSwapped ? set.player2Score : set.player1Score;
          const player2Score = sidesSwapped ? set.player1Score : set.player2Score;
          const player1Service = sidesSwapped ? set.player2Service : set.player1Service;
          const player2Service = sidesSwapped ? set.player1Service : set.player2Service;
          const player1Won = sidesSwapped ? set.winner === 2 : set.winner === 1;
          const player2Won = sidesSwapped ? set.winner === 1 : set.winner === 2;

          return (
            <div key={index} className={`set-section ${(index === 2 && set3Disabled) ? 'disabled' : ''} ${set.locked ? 'completed' : ''}`}>
              <h3 className="set-header">Set {index + 1}</h3>
              
              <div className="set-controls-row">
                {/* Left Player Controls */}
                <div className="player-controls-left">
                  <button 
                    className="control-btn-primary"
                    onClick={() => handleScoreChange(index, effectivePlayer1, 'increment')}
                    disabled={(index === 2 && set3Disabled) || matchComplete || set.locked}
                  >
                    +
                  </button>
                  <button 
                    className="control-btn-primary"
                    onClick={() => handleScoreChange(index, effectivePlayer1, 'decrement')}
                    disabled={(index === 2 && set3Disabled) || matchComplete || set.locked}
                  >
                    −
                  </button>
                  <div className={`score-box-main ${player1Won ? 'winner' : ''}`}>
                    <span className="score-text">{player1Score.toString().padStart(2, '0')}</span>
                    {player1Service && <span className="shuttle-badge">🏸</span>}
                  </div>
                </div>

                {/* Center Arrows */}
                <div className="arrows-center">
                  <div className="arrow-icon">→</div>
                  <div className="arrow-icon">←</div>
                </div>

                {/* Right Player Controls */}
                <div className="player-controls-right">
                  <div className={`score-box-main ${player2Won ? 'winner' : ''}`}>
                    <span className="score-text">{player2Score.toString().padStart(2, '0')}</span>
                    {player2Service && <span className="shuttle-badge">🏸</span>}
                  </div>
                  <button 
                    className="control-btn-primary"
                    onClick={() => handleScoreChange(index, effectivePlayer2, 'decrement')}
                    disabled={(index === 2 && set3Disabled) || matchComplete || set.locked}
                  >
                    −
                  </button>
                  <button 
                    className="control-btn-primary"
                    onClick={() => handleScoreChange(index, effectivePlayer2, 'increment')}
                    disabled={(index === 2 && set3Disabled) || matchComplete || set.locked}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Action Buttons Row */}
              <div className="action-buttons-row">
                <div className="actions-left">
                  <button 
                    className={`action-btn-service ${player1Service ? 'active' : ''}`}
                    onClick={() => handleServiceToggle(index, effectivePlayer1)}
                    disabled={(index === 2 && set3Disabled) || matchComplete || set.locked}
                  >
                    Service
                  </button>
                  <button 
                    className={`action-btn-win ${player1Won ? 'active' : ''}`}
                    onClick={() => handleSetWin(index, effectivePlayer1)}
                    disabled={(index === 2 && set3Disabled) || matchComplete || set.locked}
                  >
                    Win
                  </button>
                </div>
                <div className="actions-right">
                  <button 
                    className={`action-btn-win ${player2Won ? 'active' : ''}`}
                    onClick={() => handleSetWin(index, effectivePlayer2)}
                    disabled={(index === 2 && set3Disabled) || matchComplete || set.locked}
                  >
                    Win
                  </button>
                  <button 
                    className={`action-btn-service ${player2Service ? 'active' : ''}`}
                    onClick={() => handleServiceToggle(index, effectivePlayer2)}
                    disabled={(index === 2 && set3Disabled) || matchComplete || set.locked}
                  >
                    Service
                  </button>
                </div>
              </div>

              {index === 2 && set3Disabled && (
                <div className="alert alert-info text-center mt-3 mb-0">
                  <small>Set 3 not required - Same player won Sets 1 & 2</small>
                </div>
              )}
            </div>
          );
        })}

        {/* Cards Section with Buttons */}
        <div className="cards-section-simple">
          <h5 className="section-title">Umpire Cards</h5>
          
          <div className="cards-buttons-row">
            {cardTypes.map((card, idx) => (
              <button
                key={idx}
                className="card-display-btn"
                style={{ backgroundColor: card.color }}
                onClick={() => {
                  setSelectedCard(card.name);
                  setShowCardModal(true);
                }}
                disabled={matchComplete}
              >
                <div className="card-visual"></div>
              </button>
            ))}
          </div>

          {/* Card History */}
          {cardActions.length > 0 && (
            <div className="card-history-simple">
              <h6 className="mb-3">Card History</h6>
              <div className="table-responsive">
                <table className="table table-sm table-bordered">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Card</th>
                      <th>Player</th>
                      <th>Reason</th>
                      <th width="80">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cardActions.map((action) => (
                      <tr key={action.id}>
                        <td>{action.timestamp}</td>
                        <td>
                          <span 
                            className="badge"
                            style={{ 
                              backgroundColor: action.card.color,
                              color: 'white'
                            }}
                          >
                            {action.card.name}
                          </span>
                        </td>
                        <td>{action.player}</td>
                        <td>{action.reason}</td>
                        <td className="text-center">
                          <button 
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteCard(action.id)}
                            disabled={matchComplete}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Comments Section */}
        <div className="comments-panel">
          <h6 className="mb-3">Match Comments</h6>
          <textarea
            className="form-control"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Add any comments about the match..."
            rows="4"
            disabled={matchComplete}
          />
        </div>

        {/* Complete Button */}
        <div className="text-center mt-4">
          <button 
            className={`complete-match-btn ${matchComplete ? 'completed' : ''}`}
            onClick={handleMatchComplete}
            disabled={matchComplete}
          >
            {matchComplete ? '✓ Match Completed' : 'Complete & Save Match'}
          </button>
        </div>
      </div>

      {/* Card Modal */}
      {showCardModal && (
        <div className="card-modal-overlay" onClick={() => setShowCardModal(false)}>
          <div className="card-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="card-modal-header" style={{ 
              backgroundColor: cardTypes.find(c => c.name === selectedCard)?.color || '#4a7fca'
            }}>
              <h5>{selectedCard}</h5>
              <button className="card-modal-close" onClick={() => setShowCardModal(false)}>×</button>
            </div>
            <div className="card-modal-body">
              <div className="mb-3">
                <label className="form-label fw-bold">Select Player</label>
                <select 
                  className="form-select form-select-lg"
                  value={selectedPlayer}
                  onChange={(e) => setSelectedPlayer(e.target.value)}
                >
                  <option value="">Choose player...</option>
                  <option value={players.player1}>
                    Player 1: {players.player1}
                  </option>
                  <option value={players.player2}>
                    Player 2: {players.player2}
                  </option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold">Select Reason</label>
                <select 
                  className="form-select form-select-lg"
                  value={cardReason}
                  onChange={(e) => setCardReason(e.target.value)}
                >
                  <option value="">Choose reason...</option>
                  {cardReasons.map((reason, idx) => (
                    <option key={idx} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="card-modal-footer">
              <button className="btn btn-secondary btn-lg" onClick={() => setShowCardModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary btn-lg" onClick={handleAddCard}>
                Add Card
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UmpireSchedule;